import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { KeyValue } from '@angular/common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Step3Model } from 'src/app/chatstyle/models/onboarding.model';
import { getBoardTerminology } from 'src/app/chatstyle/models/psa-configurations';
import { FieldType } from 'src/app/shared/dynamic-forms/enums/field-type';
import { AuthService } from 'src/app/auth/auth/auth.service';
import { ConnectorDefinition } from 'src/app/chatstyle/models/connector.model';

export interface PsaCustomizationState {
  availableBoards$: BehaviorSubject<KeyValue<string, string>[]>;
  availableSources$: BehaviorSubject<KeyValue<string, string>[]>;
  currentStatusOptions$: BehaviorSubject<KeyValue<string, string>[]>;
  currentTypeOptions$: BehaviorSubject<KeyValue<string, string>[]>;
  availablePriorities$: BehaviorSubject<KeyValue<string, string>[]>;
  // Time entry (Autotask only)
  availableResources$: BehaviorSubject<KeyValue<string, string>[]>;
  availableRoles$: BehaviorSubject<KeyValue<string, string>[]>;
}

@Injectable({
  providedIn: 'root'
})
export class PsaCustomizationService {

  private static readonly FALLBACK_OPTIONS = {
    BOARDS: [
      { key: 'Help Desk', value: 'helpdesk' },
      { key: 'Incidents', value: 'incidents' },
      { key: 'Maintenance', value: 'maintenance' },
      { key: 'Projects', value: 'projects' },
      { key: 'Sales Support', value: 'sales' }
    ],
    STATUSES: [
      { key: 'Assigned', value: 'assigned' },
      { key: 'In Progress', value: 'in-progress' },
      { key: 'New', value: 'new' },
      { key: 'Open', value: 'open' }
    ],
    TYPES: [
      { key: '<PSA Default>', value: '' }
    ],
    PRIORITIES: [
      { key: 'High', value: 'high' },
      { key: 'Medium', value: 'medium' },
      { key: 'Low', value: 'low' }
    ],
    SOURCES: [
      { key: 'CloudRadial Chat', value: 'cloudradial-chat' },
      { key: 'Email', value: 'email' },
      { key: 'Phone', value: 'phone' },
      { key: 'Portal', value: 'portal' }
    ]
  } as const;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Create a new state object for PSA customization dropdowns
   */
  createState(): PsaCustomizationState {
    return {
      availableBoards$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availableSources$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      currentStatusOptions$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      currentTypeOptions$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availablePriorities$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availableResources$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availableRoles$: new BehaviorSubject<KeyValue<string, string>[]>([])
    };
  }

  /**
   * Complete and clean up the state observables
   */
  destroyState(state: PsaCustomizationState): void {
    state.availableBoards$.complete();
    state.availableSources$.complete();
    state.currentStatusOptions$.complete();
    state.currentTypeOptions$.complete();
    state.availablePriorities$.complete();
    state.availableResources$.complete();
    state.availableRoles$.complete();
  }

  /**
   * Check if the PSA is Syncro (which has all optional fields)
   */
  isSyncroPsa(psaId: string): boolean {
    return psaId?.toLowerCase() === 'syncromsp';
  }

  /**
   * Get PSA-specific terminology for boards/queues
   */
  getBoardTerminologyLabel(psaId: string): string {
    switch (psaId?.toLowerCase()) {
      case 'autotask': return 'Queues';
      case 'bms': return 'Queues';
      case 'cwmanage': return 'Board';
      case 'freshdesk': return 'Group';
      case 'halo': return 'Board';
      case 'syncromsp': return 'Ticket List';
      default: return 'Board';
    }
  }

  /**
   * Build form fields for PSA Customization
   */
  buildFormFields(
    state: PsaCustomizationState,
    psaId: string,
    psaName: string,
    onBoardChange: (boardValue: string) => void,
    allFieldsOptional: boolean = false,
    onResourceChange: (resourceId: string) => void = () => {},
    showTimeEntrySettings: boolean = true,
    disableTimeEntrySettings: boolean = false
  ): FormlyFieldConfig[] {
    const isSyncro = this.isSyncroPsa(psaId);
    const boardLabel = this.getBoardTerminologyLabel(psaId);

    const isAutotask = psaId?.toLowerCase() === 'autotask';

    const fields: FormlyFieldConfig[] = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          // Row 1: Board + Source (2 columns)
          {
            key: 'defaultBoard',
            type: FieldType.Select,
            props: {
              label: `Default ${boardLabel}`,
              placeholder: `<Select ${boardLabel}>`,
              options: state.availableBoards$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'Where new tickets will be created by default',
              required: !isSyncro && !allFieldsOptional,
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-6 col-sm-12',
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                field.formControl?.valueChanges.subscribe((boardValue: string) => {
                  onBoardChange(boardValue);
                });
              }
            },
          },
          {
            key: 'ticketSource',
            type: FieldType.Select,
            props: {
              label: 'Ticket Source',
              placeholder: '<Select Source>',
              options: state.availableSources$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'This will be recorded in tickets for reporting purposes',
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-6 col-sm-12',
          },
          // Row 2: Type + Status + Priority (3 columns)
          {
            key: 'defaultTicketType',
            type: FieldType.Select,
            props: {
              label: 'Ticket Type',
              placeholder: '<PSA Default>',
              options: state.currentTypeOptions$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'Type of ticket to create',
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-4 col-sm-12',
          },
          {
            key: 'defaultStatus',
            type: FieldType.Select,
            props: {
              label: 'Ticket Status',
              placeholder: isSyncro ? '<PSA Default>' : '<Select Status>',
              options: state.currentStatusOptions$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'Initial status for new tickets',
              required: !isSyncro && !allFieldsOptional,
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-4 col-sm-12',
          },
          {
            key: 'defaultPriority',
            type: FieldType.Select,
            props: {
              label: 'Ticket Priority',
              placeholder: isSyncro ? '<PSA Default>' : '<Select Priority>',
              options: state.availablePriorities$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'Priority level for new tickets',
              required: !isSyncro && !allFieldsOptional,
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-4 col-sm-12',
          },
        ],
      },
    ];

    // Add time entry settings for Autotask (Pro+ only)
    if (isAutotask && showTimeEntrySettings) {
      fields.push({
        fieldGroupClassName: 'row mt-4',
        fieldGroup: [
          {
            template: '<h6 class="col-12 mt-4 mb-3 pb-2" style="font-weight: 600; font-size: 15px; border-bottom: 1px solid #edebe9;">Time Entry Settings</h6>',
            className: 'col-12',
          },
          {
            key: 'timeEntryEnabled',
            type: FieldType.Checkbox,
            props: {
              label: 'Enable automatic time entries',
              description: disableTimeEntrySettings
                ? 'This feature is available on Pro and Enterprise tiers'
                : 'Automatically create time entries on Autotask tickets for chat sessions',
              disabled: disableTimeEntrySettings,
            },
            className: 'col-12 mb-2',
          },
          {
            key: 'timeEntryDefaultResourceID',
            type: FieldType.Select,
            props: {
              label: 'Default Resource',
              placeholder: '<Select Resource>',
              options: state.availableResources$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'The resource (technician) to attribute time entries to',
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-6 col-sm-12',
            expressions: {
              'props.required': 'model.timeEntryEnabled',
              'props.disabled': '!model.timeEntryEnabled',
            },
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                field.formControl?.valueChanges.subscribe((resourceId: string) => {
                  onResourceChange(resourceId);
                });
              }
            },
          },
          {
            key: 'timeEntryDefaultRoleID',
            type: FieldType.Select,
            props: {
              label: 'Default Role',
              placeholder: '<Select Role>',
              options: state.availableRoles$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'The role for the resource on time entries',
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-6 col-sm-12',
            expressions: {
              'props.disabled': '!model.timeEntryEnabled',
            },
          },
        ],
      });
    }

    return fields;
  }

  /**
   * Create an empty Step3Model
   */
  createEmptyModel(): Step3Model {
    return {
      defaultBoard: '',
      defaultStatus: '',
      defaultTicketType: '',
      defaultPriority: '',
      ticketSource: '',
      allowedBoards: [],
      enablePriorityMapping: false,
      autoAssign: false,
      notifyCreation: true,
      syncStatus: true,
      timeEntryEnabled: false,
      timeEntryDefaultResourceID: '',
      timeEntryDefaultRoleID: '',
    };
  }

  /**
   * Generic method to transform API responses to KeyValue format
   */
  transformApiResponseToKeyValuePairs(apiResponse: any): KeyValue<string, string>[] {
    let items: any[] = [];

    if (apiResponse?.data && Array.isArray(apiResponse.data)) {
      items = apiResponse.data;
    } else if (Array.isArray(apiResponse)) {
      items = apiResponse;
    } else {
      return [];
    }

    return items
      .filter((item: any) => (item.Name || item.name) && (item.Value || item.value))
      .map((item: any) => ({
        key: item.Name || item.name,
        value: item.Name || item.name
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  /**
   * Call PSA connector API with specified type and optional parameters
   */
  callConnectorApi(connector: ConnectorDefinition, type: string, parameters?: any): Observable<any> {
    if (!connector?.connectorID) {
      return throwError(() => new Error('Connector not properly configured'));
    }

    const baseUrl = this.getConnectorBaseUrl(connector);
    if (!baseUrl) {
      return throwError(() => new Error('Connector base URL not configured'));
    }

    const userId = this.authService.getClaims()?.sub;
    if (!userId) {
      return throwError(() => new Error('User authentication required'));
    }

    const psaId = connector.connectorDefinitionID;
    const mappedType = getBoardTerminology(type, psaId, connector.connectorID);

    let apiUrl = `${baseUrl}/api/data/${connector.userID}/${connector.connectorID}/?type=${mappedType}`;

    if (parameters) {
      Object.keys(parameters).forEach(key => {
        apiUrl += `&${key}=${encodeURIComponent(parameters[key])}`;
      });
    }

    const headers = {
      'Authorization': this.authService.getAuthorizationHeaderValue(),
      'Content-Type': 'application/json'
    };

    return this.http.get(apiUrl, { headers }).pipe(
      catchError((error: any) => {
        console.error(`Error calling connector API with type ${type}:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the base URL from connector configuration
   * Uses connector's baseUrl or derives from testUrl
   */
  private getConnectorBaseUrl(connector: ConnectorDefinition): string | null {
    if (!connector) {
      return null;
    }

    // Use baseUrl if available
    if (connector.baseUrl?.trim()) {
      return connector.baseUrl.trim();
    }

    // Fallback: derive base URL from testUrl
    if (connector.testUrl?.trim()) {
      try {
        const url = new URL(connector.testUrl.trim());
        return `${url.protocol}//${url.host}`;
      } catch (error) {
        console.error('Error parsing testUrl:', error);
      }
    }

    return null;
  }

  /**
   * Load boards/queues from PSA connector
   */
  loadBoards(connector: ConnectorDefinition, state: PsaCustomizationState, forceRefresh = false): Observable<any> {
    return this.callConnectorApi(connector, 'board', forceRefresh ? { refresh: 'true' } : undefined).pipe(
      tap((metadata: any) => {
        let boards: any[] = [];
        if (metadata?.data) {
          boards = metadata.data;
        } else if (Array.isArray(metadata)) {
          boards = metadata;
        }
        const processedBoards = this.transformApiResponseToKeyValuePairs(boards);
        // Use fallback if API returns empty array
        if (processedBoards.length > 0) {
          state.availableBoards$.next(processedBoards);
        } else {
          state.availableBoards$.next([...PsaCustomizationService.FALLBACK_OPTIONS.BOARDS]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading boards:', error);
        state.availableBoards$.next([...PsaCustomizationService.FALLBACK_OPTIONS.BOARDS]);
        return of(null);
      })
    );
  }

  /**
   * Load ticket statuses from PSA connector based on selected board
   */
  loadStatuses(connector: ConnectorDefinition, state: PsaCustomizationState, boardName?: string): Observable<any> {
    if (!boardName) {
      state.currentStatusOptions$.next([]);
      return of(null);
    }

    return this.callConnectorApi(connector, 'status', { name: boardName }).pipe(
      tap((statuses: any) => {
        if (statuses && Array.isArray(statuses)) {
          const processedStatuses = this.transformApiResponseToKeyValuePairs(statuses);
          // Use fallback if API returns empty array
          if (processedStatuses.length > 0) {
            state.currentStatusOptions$.next(processedStatuses);
          } else {
            state.currentStatusOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.STATUSES]);
          }
        } else {
          state.currentStatusOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.STATUSES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading statuses:', error);
        state.currentStatusOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.STATUSES]);
        return of(null);
      })
    );
  }

  /**
   * Load ticket sources from PSA connector
   */
  loadSources(connector: ConnectorDefinition, state: PsaCustomizationState, model: Step3Model, forceRefresh = false): Observable<any> {
    return this.callConnectorApi(connector, 'source', forceRefresh ? { refresh: 'true' } : undefined).pipe(
      tap((sources: any) => {
        if (sources && Array.isArray(sources)) {
          const processedSources = this.transformApiResponseToKeyValuePairs(sources);
          if (!model.ticketSource && processedSources.length > 0) {
            const defaultSource = processedSources.find(s =>
              s.key.toLowerCase().includes('cloudradial') ||
              s.key.toLowerCase().includes('chat')
            );
            model.ticketSource = defaultSource ? defaultSource.value : processedSources[0].value;
          }
          state.availableSources$.next(processedSources);
        } else {
          if (!model.ticketSource) {
            model.ticketSource = 'cloudradial-chat';
          }
          state.availableSources$.next([...PsaCustomizationService.FALLBACK_OPTIONS.SOURCES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading sources:', error);
        if (!model.ticketSource) {
          model.ticketSource = 'cloudradial-chat';
        }
        state.availableSources$.next([...PsaCustomizationService.FALLBACK_OPTIONS.SOURCES]);
        return of(null);
      })
    );
  }

  /**
   * Load ticket types from PSA connector based on selected board
   */
  loadTypes(connector: ConnectorDefinition, state: PsaCustomizationState, boardName?: string): Observable<any> {
    if (!boardName) {
      state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
      return of(null);
    }

    return this.callConnectorApi(connector, 'type', { name: boardName }).pipe(
      tap((types: any) => {
        if (types && Array.isArray(types)) {
          const processedTypes = this.transformApiResponseToKeyValuePairs(types);
          // Use fallback if API returns empty array
          if (processedTypes.length > 0) {
            state.currentTypeOptions$.next(processedTypes);
          } else {
            state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
          }
        } else {
          state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading types:', error);
        state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
        return of(null);
      })
    );
  }

  /**
   * Load priorities from PSA connector
   */
  /**
   * Transform API response to KeyValue pairs using the actual Value field (not Name).
   * Used for resources/roles where the value is a numeric ID that must be preserved.
   */
  private transformApiResponseWithIds(apiResponse: any): KeyValue<string, string>[] {
    let items: any[] = [];

    if (apiResponse?.data && Array.isArray(apiResponse.data)) {
      items = apiResponse.data;
    } else if (Array.isArray(apiResponse)) {
      items = apiResponse;
    } else {
      return [];
    }

    return items
      .filter((item: any) => (item.Name ?? item.name) != null && (item.Value ?? item.value) != null)
      .map((item: any) => ({
        key: item.Name ?? item.name,
        value: String(item.Value ?? item.value)
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  /**
   * Load resources from Autotask connector (for time entry config)
   */
  loadResources(connector: ConnectorDefinition, state: PsaCustomizationState, forceRefresh = false): Observable<any> {
    return this.callConnectorApi(connector, 'resource', forceRefresh ? { refresh: 'true' } : undefined).pipe(
      tap((response: any) => {
        const resources = this.transformApiResponseWithIds(response);
        state.availableResources$.next(resources);
      }),
      catchError((error: any) => {
        console.error('Error loading resources:', error);
        state.availableResources$.next([]);
        return of(null);
      })
    );
  }

  /**
   * Load roles for a specific resource from Autotask connector (for time entry config)
   */
  loadRoles(connector: ConnectorDefinition, state: PsaCustomizationState, resourceId: string): Observable<any> {
    if (!resourceId) {
      state.availableRoles$.next([]);
      return of(null);
    }

    return this.callConnectorApi(connector, 'role', { name: resourceId }).pipe(
      tap((response: any) => {
        const roles = this.transformApiResponseWithIds(response);
        state.availableRoles$.next(roles);
      }),
      catchError((error: any) => {
        console.error('Error loading roles:', error);
        state.availableRoles$.next([]);
        return of(null);
      })
    );
  }

  loadPriorities(connector: ConnectorDefinition, state: PsaCustomizationState, psaId: string, forceRefresh = false, boardName?: string): Observable<any> {
    const isSyncro = this.isSyncroPsa(psaId);
    const params = boardName
      ? { name: boardName, ...(forceRefresh ? { refresh: 'true' } : {}) }
      : (forceRefresh ? { refresh: 'true' } : undefined);
    return this.callConnectorApi(connector, 'priority', params).pipe(
      tap((priorities: any) => {
        if (priorities && Array.isArray(priorities)) {
          const processedPriorities = this.transformApiResponseToKeyValuePairs(priorities);
          const filteredPriorities = processedPriorities.filter(p =>
            !p.key.toLowerCase().includes('psa default')
          );
          // Use fallback if API returns empty array
          if (filteredPriorities.length > 0) {
            const priorityOptions = isSyncro
              ? [{ key: '<PSA Default>', value: '' }, ...filteredPriorities]
              : filteredPriorities;
            state.availablePriorities$.next(priorityOptions);
          } else {
            state.availablePriorities$.next([...PsaCustomizationService.FALLBACK_OPTIONS.PRIORITIES]);
          }
        } else {
          state.availablePriorities$.next([...PsaCustomizationService.FALLBACK_OPTIONS.PRIORITIES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading priorities:', error);
        state.availablePriorities$.next([...PsaCustomizationService.FALLBACK_OPTIONS.PRIORITIES]);
        return of(null);
      })
    );
  }
}
